import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const MyPage: React.FC = () => {
  const navigate = useNavigate();
  const { member, logout, updateProfile, changePassword } = useAuth();
  const [tab, setTab] = useState<'profile' | 'address' | 'password'>('profile');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 프로필 폼
  const [profileForm, setProfileForm] = useState({ name: '', phone: '' });
  // 배송지 폼
  const [addressForm, setAddressForm] = useState({ address: '', detail_address: '', postal_code: '', pccc: '' });
  // 비밀번호 폼
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });

  useEffect(() => {
    if (!member) {
      navigate('/login');
      return;
    }
    setProfileForm({ name: member.name || '', phone: member.phone || '' });
    setAddressForm({
      address: member.address || '',
      detail_address: member.detail_address || '',
      postal_code: member.postal_code || '',
      pccc: member.pccc || ''
    });
  }, [member, navigate]);

  const clearMsg = () => { setMsg(''); setError(''); };

  const handleAddressSearch = () => {
    const w = window as any;
    const run = () => {
      new w.daum.Postcode({
        oncomplete: (data: any) => {
          setAddressForm(prev => ({
            ...prev,
            postal_code: data.zonecode,
            address: data.roadAddress || data.jibunAddress
          }));
          // 상세주소 입력란에 포커스
          setTimeout(() => {
            const el = document.querySelector('input[placeholder="101동 1001호"]') as HTMLInputElement;
            if (el) el.focus();
          }, 100);
        }
      }).open();
    };
    if (w.daum && w.daum.Postcode) {
      run();
    } else {
      const script = document.createElement('script');
      script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
      script.onload = run;
      document.head.appendChild(script);
    }
  };

  const handleProfileSave = async () => {
    clearMsg();
    if (!profileForm.name.trim()) { setError('이름을 입력해주세요'); return; }
    setLoading(true);
    const result = await updateProfile({ name: profileForm.name, phone: profileForm.phone });
    setLoading(false);
    result.success ? setMsg('프로필이 수정되었습니다') : setError(result.error || '수정 실패');
  };

  const handleAddressSave = async () => {
    clearMsg();
    setLoading(true);
    const result = await updateProfile(addressForm);
    setLoading(false);
    result.success ? setMsg('배송지가 저장되었습니다') : setError(result.error || '저장 실패');
  };

  const handlePasswordChange = async () => {
    clearMsg();
    if (pwForm.newPw.length < 6) { setError('새 비밀번호는 6자 이상이어야 합니다'); return; }
    if (pwForm.newPw !== pwForm.confirm) { setError('새 비밀번호가 일치하지 않습니다'); return; }
    setLoading(true);
    const result = await changePassword(pwForm.current, pwForm.newPw);
    setLoading(false);
    if (result.success) {
      setMsg('비밀번호가 변경되었습니다');
      setPwForm({ current: '', newPw: '', confirm: '' });
    } else {
      setError(result.error || '변경 실패');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!member) return null;

  return (
    <>
      <Header />
      <div className="mypage">
        <div className="mypage-inner">
          <div className="mypage-sidebar">
            <div className="mypage-user">
              <div className="mypage-avatar">
                {member.name.charAt(0)}
              </div>
              <div className="mypage-username">{member.name}</div>
              <div className="mypage-email">{member.email}</div>
            </div>
            <nav className="mypage-nav">
              <button className={tab === 'profile' ? 'active' : ''} onClick={() => { setTab('profile'); clearMsg(); }}>
                내 정보
              </button>
              <button className={tab === 'address' ? 'active' : ''} onClick={() => { setTab('address'); clearMsg(); }}>
                배송지 관리
              </button>
              <button className={tab === 'password' ? 'active' : ''} onClick={() => { setTab('password'); clearMsg(); }}>
                비밀번호 변경
              </button>
              <button className="logout-btn" onClick={handleLogout}>로그아웃</button>
            </nav>
          </div>

          <div className="mypage-content">
            {msg && <div className="auth-success">{msg}</div>}
            {error && <div className="auth-error">{error}</div>}

            {tab === 'profile' && (
              <div className="mypage-section">
                <h3>내 정보</h3>
                <div className="auth-field">
                  <label>이메일</label>
                  <input type="email" value={member.email} disabled />
                </div>
                <div className="auth-field">
                  <label>이름</label>
                  <input type="text" value={profileForm.name}
                    onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} />
                </div>
                <div className="auth-field">
                  <label>연락처</label>
                  <input type="tel" value={profileForm.phone}
                    onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                    placeholder="010-0000-0000" />
                </div>
                <button className="auth-btn" onClick={handleProfileSave} disabled={loading}>
                  {loading ? '저장 중...' : '저장'}
                </button>
              </div>
            )}

            {tab === 'address' && (
              <div className="mypage-section">
                <h3>배송지 관리</h3>
                <div className="auth-field">
                  <label>우편번호</label>
                  <div className="address-search-row">
                    <input type="text" value={addressForm.postal_code}
                      onChange={e => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                      placeholder="12345" readOnly />
                    <button type="button" className="address-search-btn" onClick={handleAddressSearch}>
                      주소 검색
                    </button>
                  </div>
                </div>
                <div className="auth-field">
                  <label>주소</label>
                  <input type="text" value={addressForm.address}
                    onChange={e => setAddressForm({ ...addressForm, address: e.target.value })}
                    placeholder="주소 검색 버튼을 클릭하세요" readOnly />
                </div>
                <div className="auth-field">
                  <label>상세주소</label>
                  <input type="text" value={addressForm.detail_address}
                    onChange={e => setAddressForm({ ...addressForm, detail_address: e.target.value })}
                    placeholder="101동 1001호" />
                </div>
                <div className="auth-field">
                  <label>개인통관고유부호</label>
                  <div className="address-search-row">
                    <input type="text" value={addressForm.pccc}
                      onChange={e => {
                        let v = e.target.value.toUpperCase().replace(/[^P0-9-]/g, '');
                        if (v && !v.startsWith('P')) v = 'P' + v;
                        setAddressForm({ ...addressForm, pccc: v });
                      }}
                      placeholder="P000-0000-000000"
                      maxLength={16} />
                    <button type="button" className="address-search-btn"
                      onClick={() => window.open('https://unipass.customs.go.kr/csp/pers498/retrieve.do', '_blank')}>
                      조회하기
                    </button>
                  </div>
                  {addressForm.pccc && !/^P\d{3}-\d{4}-\d{6}$/.test(addressForm.pccc) && (
                    <span className="field-hint field-hint-warn">형식: P000-0000-000000 (P + 숫자 13자리)</span>
                  )}
                  {addressForm.pccc && /^P\d{3}-\d{4}-\d{6}$/.test(addressForm.pccc) && (
                    <span className="field-hint field-hint-ok">형식이 올바릅니다</span>
                  )}
                  <span className="field-hint">해외직구 통관 시 필요합니다. 모르시면 '조회하기'를 클릭하세요.</span>
                </div>
                <button className="auth-btn" onClick={handleAddressSave} disabled={loading}>
                  {loading ? '저장 중...' : '저장'}
                </button>
              </div>
            )}

            {tab === 'password' && (
              <div className="mypage-section">
                <h3>비밀번호 변경</h3>
                <div className="auth-field">
                  <label>현재 비밀번호</label>
                  <input type="password" value={pwForm.current}
                    onChange={e => setPwForm({ ...pwForm, current: e.target.value })} />
                </div>
                <div className="auth-field">
                  <label>새 비밀번호</label>
                  <input type="password" value={pwForm.newPw}
                    onChange={e => setPwForm({ ...pwForm, newPw: e.target.value })}
                    placeholder="6자 이상" />
                </div>
                <div className="auth-field">
                  <label>새 비밀번호 확인</label>
                  <input type="password" value={pwForm.confirm}
                    onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} />
                </div>
                <button className="auth-btn" onClick={handlePasswordChange} disabled={loading}>
                  {loading ? '변경 중...' : '비밀번호 변경'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MyPage;
